import React from 'react';
import { Link } from 'react-router-dom';
import { Wine, MapPin } from 'lucide-react';
import { Card } from './ui/card';
import { Badge } from './ui/badge';

const WineDealCard = ({ deal, position }) => {
  // Extract vintage from name or use vintage field
  const getVintage = () => {
    if (deal.vintage) return deal.vintage;
    const match = deal.name?.match(/^(19|20)\d{2}/);
    return match ? match[0] : null;
  };

  const vintage = getVintage();

  return (
    <Link 
      to={`/${deal.id}`}
      className="block no-underline"
      style={{ textDecoration: 'none', color: 'inherit' }}
      data-testid={`wine-card-link-${deal.id}`}
    >
      <Card 
        className="w-full transition-all duration-300 hover:shadow-lg hover:-translate-y-1 overflow-hidden cursor-pointer"
        style={{ borderColor: 'rgb(169, 141, 140)' }}
      >
        <div className="flex flex-col sm:flex-row">
          {/* Image Section */}
          <div className="w-full sm:w-64 flex-shrink-0 relative">
            <div className="w-full h-64 sm:h-[250px] bg-gray-100 flex items-center justify-center relative overflow-hidden">
              {deal.image_url ? (
                <img
                  src={deal.image_url}
                  alt={deal.name}
                  className="w-full h-full object-contain"
                  onError={(e) => {
                    e.target.style.display = 'none';
                    e.target.nextSibling.style.display = 'flex';
                  }}
                />
              ) : null}
              <div 
                className={`${deal.image_url ? 'hidden' : 'flex'} w-full h-full items-center justify-center text-gray-400 absolute inset-0 bg-gray-100`}
              >
                <Wine className="w-16 h-16" />
              </div>
              
              {/* Position Badge - Top Left */}
              {position && (
                <div className="absolute top-3 left-3 z-10">
                  <Badge 
                    className="text-sm font-bold shadow-lg text-white border"
                    style={{ backgroundColor: 'rgb(93, 15, 15)', borderColor: 'rgb(93, 15, 15)' }}
                  >
                    #{position}
                  </Badge>
                </div>
              )}
              
              {/* Rating Badge - Top Right */}
              {deal.rating && (
                <div className="absolute top-3 right-3">
                  <Badge className="text-sm font-bold shadow-lg bg-white/90 text-gray-800 border border-gray-200">
                    {deal.rating}
                  </Badge>
                </div>
              )}
            </div>
          </div>

          {/* Content Section */}
          <div className="flex-1 flex flex-col min-h-[250px] sm:h-[250px] p-4">
            {/* Title */}
            <div className="tracking-tight text-lg font-bold text-gray-900 leading-tight line-clamp-2 mb-3">
              {deal.name}
            </div>

            <div className="flex-1 flex flex-col justify-between">
              <div className="space-y-4 flex-1">
                {/* Wine Type, Vintage, Country Row */}
                <div className="flex items-center text-sm text-gray-700 flex-wrap gap-2">
                  {deal.wine_type && (
                    <div className="flex items-center">
                      <Wine className="w-4 h-4 mr-1 text-purple-500" />
                      <span className="font-medium">{deal.wine_type}</span>
                    </div>
                  )}
                  {vintage && (
                    <>
                      <span className="text-gray-400">•</span>
                      <span className="font-medium">{vintage}</span>
                    </>
                  )}
                  {deal.country && (
                    <>
                      <span className="text-gray-400">•</span>
                      <div className="flex items-center">
                        <MapPin className="w-4 h-4 mr-1 text-green-500" />
                        <span className="font-medium">{deal.country}</span>
                      </div>
                    </>
                  )}
                </div>

                {/* Price */}
                <div className="flex items-center">
                  <span className="text-3xl font-bold" style={{ color: 'rgb(93, 15, 15)' }}>
                    ${Math.round(deal.price)}
                  </span>
                </div>

                {/* Variety */}
                {deal.variety && (
                  <div className="text-sm text-gray-700">
                    <span className="font-medium">Variety: </span>
                    <span>{deal.variety}</span>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </Card>
    </Link>
  );
};

export default WineDealCard;
